import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { menuCategories, drinks } from '../data/menuData';
import { Flame, Download } from 'lucide-react';
import { Star } from './MemphisShapes';

const MenuCard = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.03 }}
    whileHover={{ scale: 1.02, y: -4 }}
    className={`card-brutal bg-white p-4 md:p-5 ${item.isSpecial ? 'ring-4 ring-santomato-yellow' : ''}`}
    data-testid={`menu-item-${item.id}`}
  >
    <div className="flex justify-between items-start gap-3">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-fredoka font-bold text-lg md:text-xl text-black uppercase">
            {item.name}
          </h4>
          {item.isSpecial && (
            <span className="bg-santomato-yellow border-2 border-black px-2 py-0.5 rounded-full">
              <span className="font-fredoka text-xs font-bold">SPECIALE</span>
            </span>
          )}
        </div>
        <p className="font-nunito text-sm text-gray-600">
          {item.description}
        </p>
      </div>
      <span className="font-fredoka font-bold text-xl md:text-2xl text-santomato-red whitespace-nowrap">
        €{item.price.toFixed(2)}
      </span>
    </div>
  </motion.div>
);

const CategoryTab = ({ category, isActive, onClick, label }) => (
  <button
    onClick={onClick}
    data-testid={`menu-tab-${category}`}
    className={`font-fredoka font-bold text-sm md:text-base uppercase px-4 md:px-6 py-3 border-4 border-black rounded-full transition-all whitespace-nowrap
      ${isActive 
        ? 'bg-santomato-yellow text-black shadow-brutal' 
        : 'bg-white text-black hover:bg-gray-100'
      }`}
  >
    {label}
  </button>
);

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState('tradizione');
  
  const categories = [
    { key: 'tradizione', label: 'Pizze Tradizionali' },
    { key: 'territorio', label: 'Pizze del Territorio' },
    { key: 'signature', label: 'Pizze Signature' },
    { key: 'fritti', label: 'Fritti' },
    { key: 'bibite', label: 'Bibite' }
  ];

  const scrollToBurger = () => {
    if (window.__lenis) {
      window.__lenis.scrollTo('#burger');
    } else {
      document.getElementById('burger')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentCategory = menuCategories[activeCategory];

  return (
    <section 
      id="menu"
      data-testid="menu-section"
      className="min-h-screen bg-santomato-red py-16 md:py-24 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <Star className="absolute top-20 left-8 opacity-30" color="#FFD600" size={60} />
      <Star className="absolute bottom-32 right-12 opacity-30" color="#FFD600" size={45} />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-14"
        >
          <h2 
            className="font-fredoka font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white uppercase mb-4"
            data-testid="menu-title"
          >
            IL MENU
          </h2>
          <p className="font-nunito font-bold text-lg md:text-xl text-santomato-yellow">
            Pizza grande e generosa • Sottile e croccante
          </p>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10 md:mb-14">
          {categories.map((cat) => (
            <CategoryTab
              key={cat.key}
              category={cat.key}
              label={cat.label}
              isActive={activeCategory === cat.key}
              onClick={() => setActiveCategory(cat.key)}
            />
          ))}
          <button
            onClick={scrollToBurger}
            data-testid="menu-tab-burger"
            className="font-fredoka font-bold text-sm md:text-base uppercase px-4 md:px-6 py-3 border-4 border-black rounded-full transition-all whitespace-nowrap bg-black text-santomato-yellow hover:bg-santomato-yellow hover:text-black flex items-center gap-2"
          >
            <Flame size={18} strokeWidth={2.5} />
            Burger
          </button>
        </div>

        {/* Menu Content */}
        <AnimatePresence mode="wait">
          {activeCategory === 'bibite' ? (
            <motion.div
              key="bibite"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {/* Free Refill Card */}
              <div className="card-brutal bg-santomato-yellow p-6 md:col-span-2 lg:col-span-1">
                <h3 className="font-fredoka font-bold text-2xl text-black uppercase mb-2">
                  {drinks.bibite.name}
                </h3>
                <p className="font-fredoka font-bold text-4xl text-santomato-red mb-3">
                  €{drinks.bibite.price.toFixed(2)}
                </p>
                <p className="font-nunito text-sm text-gray-800 mb-4">
                  {drinks.bibite.note}
                </p>
                <div className="flex flex-wrap gap-2">
                  {drinks.bibite.brands.map((brand, i) => (
                    <span key={i} className="bg-black text-white font-nunito font-bold text-xs px-3 py-1 rounded-full">
                      {brand}
                    </span>
                  ))}
                </div>
              </div>

              {/* Beers */}
              {drinks.birre.items.map((beer, index) => (
                <div key={beer.id} className="card-brutal bg-white p-6" data-testid={`drink-${beer.id}`}>
                  <h4 className="font-fredoka font-bold text-lg text-black uppercase mb-1">
                    {beer.name}
                  </h4>
                  <p className="font-nunito text-sm text-gray-600 mb-3">
                    {beer.description}
                  </p>
                  {beer.isSpina ? (
                    <div className="flex gap-4">
                      <span className="font-fredoka font-bold text-santomato-red">
                        Piccola €{beer.prices.piccola.toFixed(2)}
                      </span>
                      <span className="font-fredoka font-bold text-santomato-red">
                        Media €{beer.prices.media.toFixed(2)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="font-fredoka font-bold text-2xl text-santomato-red">
                        €{beer.price.toFixed(2)}
                      </span>
                      <span className="font-nunito text-sm text-gray-500">{beer.size}</span>
                    </div>
                  )}
                </div>
              ))}

              {/* Lattine */}
              <div className="card-brutal bg-white p-6">
                <h4 className="font-fredoka font-bold text-lg text-black uppercase mb-1">
                  {drinks.lattine.name}
                </h4>
                <span className="font-fredoka font-bold text-2xl text-santomato-red">
                  €{drinks.lattine.price.toFixed(2)}
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Category description */}
              <div className="text-center mb-8">
                <h3 className="font-fredoka font-bold text-2xl md:text-3xl text-white uppercase mb-2">
                  {currentCategory.name}
                </h3>
                <p className="font-nunito text-santomato-yellow">
                  {currentCategory.description}
                </p>
              </div>

              {/* Items grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentCategory.items.map((item, index) => (
                  <MenuCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Extra info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="font-nunito text-white text-sm md:text-base">
            Pizza Baby +€0,50 • Servizio e Coperto €2,50
          </p>

          {/* Download takeaway menu */}
          <motion.a
            href="/menu-asporto-santomato.pdf"
            download="Menu-Asporto-Santomato.pdf"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            data-testid="menu-download-button"
            className="btn-brutal inline-flex items-center gap-3 bg-santomato-yellow text-black font-fredoka text-base md:text-lg px-8 py-4 rounded-full mt-8"
          >
            <Download size={24} strokeWidth={2.5} />
            SCARICA IL MENÙ ASPORTO
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Menu;
